/**
 * Adaptive DPR (Device Pixel Ratio) calculation
 * Ensures performance on high-resolution displays (4K) by limiting total rendered pixels
 *
 * Strategy: Calculate total pixels (width × height × native DPR)
 * If exceeding threshold, reduce DPR proportionally
 */

/**
 * Calculate adaptive DPR based on viewport dimensions
 * Allows DPR up to 1.5 on smaller screens while capping at maxPixels
 * @param {number} maxPixels - Maximum allowed pixels (default: 2.5M)
 * @param {number} maxDPR - Maximum DPR allowed (default: 1.5)
 * @returns {number} Adaptive DPR value (range: 0.5 to maxDPR)
 */
export function calculateAdaptiveDPR(maxPixels = 2500000, maxDPR = 1.5) {
  const width = window.innerWidth;
  const height = window.innerHeight;
  const nativeDPR = window.devicePixelRatio || 1;

  // Calculate what DPR would give us exactly the maxPixels limit
  // Example results with 2.5M pixel limit and maxDPR=1.5:
  // - 1K (1920x1080): idealDPR = 1.21 → adaptiveDPR = 1.21 (within 1.5 limit)
  // - 1440p (2560x1440): idealDPR = 0.68 → adaptiveDPR = 0.68
  // - 4K (3840x2160): idealDPR = 0.30 → adaptiveDPR = 0.50 (clamped to minimum)
  // - 5K (5120x2880): idealDPR = 0.17 → adaptiveDPR = 0.50 (clamped to minimum)

  const idealDPR = maxPixels / (width * height);

  // Clamp DPR between 0.5 (minimum for reasonable quality) and maxDPR (default 1.5)
  // This allows:
  // - Super high quality (DPR up to 1.5) on small screens like laptops
  // - Reduced quality (DPR down to 0.5) on 4K/5K displays to maintain performance
  const adaptiveDPR = Math.max(0.8, Math.min(maxDPR, idealDPR));

  return adaptiveDPR;
}

/**
 * Calculate adaptive DPR for interaction states (when dragging/sliding)
 * Uses proportionally reduced DPR for better performance during interactions
 * @param {number} baseDPR - The adaptive DPR for idle state
 * @param {number} reductionFactor - How much to reduce (default: 0.7)
 * @returns {number} Reduced DPR for interactions
 */
export function calculateInteractionDPR(baseDPR, reductionFactor = 0.9) {
  return Math.max(0.9, baseDPR * reductionFactor);
}

/**
 * Get optimal DPR based on current state
 * @param {boolean} isInteracting - Whether user is interacting (dragging, etc.)
 * @param {number} maxPixels - Maximum allowed pixels (default: 2.5M)
 * @param {number} maxDPR - Maximum DPR allowed (default: 1.5)
 * @returns {number} Optimal DPR for current state
 */
export function getOptimalDPR(isInteracting = false, maxPixels = 2500000, maxDPR = 1.5) {
  const adaptiveDPR = calculateAdaptiveDPR(maxPixels, maxDPR);

  if (isInteracting) {
    return calculateInteractionDPR(adaptiveDPR);
  }

  return adaptiveDPR;
}
